import { Link, usePage } from '@inertiajs/react';
import type { FooterData, SharedData } from '@/types';
import { EmailObfuscated } from './email-obfuscated';

// ─── Platform links ────────────────────────────────────────────────────────────

interface PlatformLink {
  label: string;
  href: string;
}

function platformLinks(locale: string): PlatformLink[] {
  // Locale slug prefix mapping for info pages
  const slugs: Record<string, Record<string, string>> = {
    'for-agencies': { de: 'fuer-agenturen', nl: 'voor-agentschappen' },
    'for-establishments': { de: 'fuer-betriebe', nl: 'voor-vestigingen' },
    'for-independents': { de: 'fuer-selbststaendige', nl: 'voor-zelfstandigen' },
    'pricing': {},
    'how-it-works': { de: 'so-funktioniert-es', nl: 'hoe-het-werkt' },
    'why-us': { de: 'warum-wir', nl: 'waarom-wij' },
  };

  const label: Record<string, Record<string, string>> = {
    'for-agencies':       { en: 'For agencies',       de: 'Für Agenturen',        nl: 'Voor agentschappen' },
    'for-establishments': { en: 'For establishments', de: 'Für Betriebe',          nl: 'Voor vestigingen' },
    'for-independents':   { en: 'For independents',   de: 'Für Selbstständige',    nl: 'Voor zelfstandigen' },
    'pricing':            { en: 'Pricing',             de: 'Preise',               nl: 'Prijzen' },
    'how-it-works':       { en: 'How it works',        de: 'So funktioniert es',   nl: 'Hoe het werkt' },
    'why-us':             { en: 'Why us',              de: 'Warum wir',            nl: 'Waarom wij' },
  };

  return Object.keys(slugs).map((canonical) => {
    const slug = (locale !== 'en' && slugs[canonical]?.[locale]) || canonical;
    const l = label[canonical]?.[locale] ?? label[canonical]?.['en'] ?? canonical;
    return { label: l, href: `/info/${slug}` };
  });
}

// ─── Legal links ───────────────────────────────────────────────────────────────

interface LegalLink {
  label: string;
  href: string;
  deOnly?: boolean;
}

function legalLinks(locale: string): LegalLink[] {
  const slugs: Record<string, Record<string, string>> = {
    'impressum':      { en: 'imprint',   nl: 'colofon' },
    'privacy-policy': { de: 'datenschutz', nl: 'privacybeleid' },
    'terms':          { de: 'agb',         nl: 'algemene-voorwaarden' },
  };

  const label: Record<string, Record<string, string>> = {
    'impressum':      { en: 'Imprint',          de: 'Impressum',      nl: 'Colofon' },
    'privacy-policy': { en: 'Privacy Policy',   de: 'Datenschutz',    nl: 'Privacybeleid' },
    'terms':          { en: 'Terms & Conditions', de: 'AGB',           nl: 'Algemene Voorwaarden' },
  };

  const links: LegalLink[] = Object.keys(slugs).map((canonical) => {
    const slug = (locale !== 'en' && slugs[canonical]?.[locale]) || canonical;
    const l = label[canonical]?.[locale] ?? label[canonical]?.['en'] ?? canonical;
    return { label: l, href: `/legal/${slug}` };
  });

  // Jugendschutz — DE only, handled separately via footer.is_germany
  links.push({ label: 'Jugendschutz', href: '/legal/jugendschutz', deOnly: true });

  return links;
}

// ─── Footer component ──────────────────────────────────────────────────────────

export function Footer() {
  const { footer, brand, locale } = usePage<SharedData>().props;

  const pl = platformLinks(locale as string);
  const ll = legalLinks(locale as string);
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-background border-t border-border/20 mt-auto">
      {/* Top grid */}
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-7">

          {/* Column 1 — Brand */}
          <div className="col-span-2 sm:col-span-3 lg:col-span-1">
            {brand.logo_url ? (
              <img
                src={brand.logo_url}
                alt={brand.name}
                className="h-8 w-auto"
              />
            ) : (
              <span className="text-lg font-bold">{brand.name}</span>
            )}

            {brand.tagline && (
              <p className="mt-2 text-sm text-muted-foreground">{brand.tagline}</p>
            )}

            {/* JusProg badge */}
            <div className="mt-4">
              <img
                src="/jusprog-age-xml-18.png"
                alt="JusProg 18+"
                className="h-8 w-auto opacity-60 hover:opacity-100 transition-opacity"
              />
            </div>

            {/* Jugendschutz notice (DE only) */}
            {footer.is_germany && (
              <div className="mt-3 rounded-md border border-border/30 p-3 text-xs text-muted-foreground">
                <p className="font-medium text-foreground">
                  Jugendschutzbeauftragter gemäß § 7 JMStV
                </p>
                {footer.jugendschutz_name && (
                  <p className="mt-0.5">{footer.jugendschutz_name}</p>
                )}
                <EmailObfuscated
                  userB64={footer.jugendschutz_email_user}
                  domainB64={footer.jugendschutz_email_domain}
                  className="mt-0.5 text-muted-foreground"
                />
              </div>
            )}
          </div>

          {/* Column 2 — Platform links */}
          <div className="col-span-1">
            <h3 className="text-sm font-semibold">Platform</h3>
            <ul className="mt-4 space-y-2">
              {pl.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Columns 3–6 — SEO city columns */}
          {footer.seo_columns.map((col) => (
            <div key={col.label} className="col-span-1">
              <h3 className="text-sm font-semibold">{col.label}</h3>
              <ul className="mt-4 grid grid-cols-2 gap-x-2 gap-y-1 sm:grid-cols-1">
                {col.links.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Column 7 — Legal & Contact */}
          <div className="col-span-1">
            <h3 className="text-sm font-semibold">Legal</h3>
            <ul className="mt-4 space-y-2">
              {ll
                .filter((link) => !link.deOnly || footer.is_germany)
                .map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
            </ul>

            <div className="mt-6">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Contact
              </p>
              <EmailObfuscated
                userB64={footer.email_user}
                domainB64={footer.email_domain}
                className="text-sm text-muted-foreground"
              />
            </div>
          </div>

        </div>
      </div>

      {/* Disclaimer */}
      <div className="border-t border-border/10">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <p className="text-xs text-muted-foreground/70 leading-relaxed">
            This website is an advertising and information resource only, and as such has no connection
            with or liability for any of the sites or individuals mentioned herein. We sell advertising
            space exclusively — we are not an escort agency, nor are we involved in escorting or
            prostitution in any way. We accept no responsibility for the content or actions of
            third-party websites or individuals that you may access via links, email, or phone contacts
            found on this portal.
          </p>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-border/10">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8 flex flex-col items-center justify-between gap-2 sm:flex-row">
          <p className="text-xs text-muted-foreground">
            &copy; {currentYear} {brand.name}. All rights reserved.
          </p>
          <p className="text-xs text-muted-foreground">
            This site contains adult content. You must be 18+ to use this platform.
          </p>
        </div>
      </div>
    </footer>
  );
}
